import { FC, useState, useEffect, useCallback } from 'react';
import { useFragment, graphql } from 'react-relay';
import { useIntl, defineMessages } from 'dibs-react-intl';
import { useDibsContracts } from 'dibs-crypto-utils/exports/useDibsContracts';
import { useWeb3Connection } from 'dibs-crypto-utils/exports/useWeb3Connection';
import { isV2Auction } from 'dibs-crypto-utils/exports/isV2Auction';

import { Button, ButtonProps } from 'dibs-elements/exports/Button';
import ExclamationCircle from 'dibs-icons/exports/legacy/ExclamationCircle';

import { NftItemTileItemDetails } from '../NftItemTileItemDetails';
import { NftItemTileAuctionDetails } from '../NftItemTileAuctionDetails';
import { NftItemTileTokenDetails } from '../NftItemTileTokenDetails';
import { NftItemTileMostRecentBidLeaderCta } from '../NftItemTileMostRecentBid';
import { useItemInternalContext } from '../../helpers/ItemInternalContext';
import { NftItemTileTooltip } from '../NftItemTileTooltip/NftItemTileTooltip';
import { useNftItemTileUpdateSubscription } from '../../subscriptions/nftItemTileUpdate';
import EndAuctionModal from '../ActionModals/EndAuctionModal';
import { NftMarketProvider } from 'dibs-crypto-utils/exports/useMarketContext';

import { CollectorTileContent_viewer$key } from './__generated__/CollectorTileContent_viewer.graphql';
import { CollectorTileContent_nftItem$key } from './__generated__/CollectorTileContent_nftItem.graphql';
import { CollectorTileContent_nftAuction$key } from './__generated__/CollectorTileContent_nftAuction.graphql';

import styles from './styles/nftItemTileContent.scss';

const viewerFragment = graphql`
    fragment CollectorTileContent_viewer on Viewer {
        nftBenignNeglect: featureFlag(feature: "nftBenignNeglect")
        ...EndAuctionModal_viewer
        ...useMarketContext_viewer
        ...NftItemTileTooltip_viewer
    }
`;

const nftItemFragment = graphql`
    fragment CollectorTileContent_nftItem on NftItem {
        ...NftItemTileItemDetails_nftItem
        ...NftItemTileAuctionDetails_nftItem
        ...NftItemTileTokenDetails_nftItem
        ...NftItemTileTooltip_nftItem
        ...useMarketContext_nftItem
        pdpUrl
        serviceId
        wasBurned
        auction: mostRecentAuction(excludeCanceled: true) {
            auctionId
            status
            contractAddress {
                address
            }
        }
        isERC1155
    }
`;

const nftAuctionFragment = graphql`
    fragment CollectorTileContent_nftAuction on NftAuction {
        ...NftItemTileAuctionDetails_nftAuction
    }
`;

const messages = defineMessages({
    auction: { id: 'nftItemTile.contentCtas.create', defaultMessage: 'Create Listing' },
    manage: { id: 'nftItemTile.contentCtas.manage', defaultMessage: 'Manage NFT' },
    settle: { id: 'nftItemTile.contentCtas.settle', defaultMessage: 'Settle Auction' },
    sell: { id: 'nftItemTile.contentCtas.sell', defaultMessage: 'Sell NFT' },
    cancel: { id: 'nftItemTile.contentCtas.cancel', defaultMessage: 'Cancel Auction' },
    bid: { id: 'nftItemTile.contentCtas.bid', defaultMessage: 'Place A Bid' },
    view: { id: 'nftItemTile.contentCtas.view', defaultMessage: 'View NFT' },
    endAuction: { id: 'nftItemTile.contentCtas.endAuction', defaultMessage: 'End Auction' },
    endAuctionError: {
        id: 'nftItemTile.contentCtas.endAuctionError',
        defaultMessage: 'There was an error in transferring the token. Please try again.',
    },
});

const Cta: FC<
    Pick<ButtonProps, 'href' | 'type' | 'onClick' | 'children' | 'className' | 'disabled'> & {
        msgKey: keyof typeof messages;
    }
> = props => (
    <Button {...props} size="medium" fullWidth>
        {useIntl().formatMessage(messages[props.msgKey])}
        {props.children}
    </Button>
);

type Props = {
    viewer: CollectorTileContent_viewer$key;
    nftItem: CollectorTileContent_nftItem$key;
    nftAuction?: CollectorTileContent_nftAuction$key | null;
    currency: string;
    fireTracking?: () => void;
    showCtas?: boolean;
    showCreator?: boolean;
    showAuction?: boolean | 'ifCreated';
    showToken?: boolean;
    isMobile: boolean;
    nftItemId?: string | null;
};

export const CollectorTileContent: FC<Props> = ({
    viewer: viewerProp,
    nftItem: nftItemProp,
    nftAuction: nftAuctionProp = null,
    fireTracking,
    currency,
    showCtas = false,
    showCreator = true,
    showAuction: showAuctionProp = true,
    isMobile,
    nftItemId,
}) => {
    const intl = useIntl();
    const { library } = useWeb3Connection();
    const [contracts] = useDibsContracts(library);
    const subscriptionItem = useNftItemTileUpdateSubscription(nftItemId);
    const viewer = useFragment(viewerFragment, viewerProp);
    const nftItem = useFragment<CollectorTileContent_nftItem$key>(
        nftItemFragment,
        subscriptionItem || nftItemProp
    );
    const nftAuction = useFragment(nftAuctionFragment, nftAuctionProp);
    const { auction, user, profile, bidder } = useItemInternalContext();
    const manageUrl = `/nft-create/${nftItem?.serviceId}/`;
    const showAuction = showAuctionProp === 'ifCreated' ? !!auction : showAuctionProp;

    const [endAuctionModalType, setEndAuctionModalType] = useState<'PENDING' | 'SUCCESS' | null>(
        null
    );
    const [endAuctionModalOpen, setEndAuctionModalOpen] = useState(false);
    const [endAuctionPending, setEndAuctionPending] = useState(false);
    const [hasEndAuctionError, setHasEndAuctionError] = useState(false);
    const [isAuctionGloballyPaused, setIsAuctionGloballyPaused] = useState(false);

    useEffect(() => {
        if (endAuctionModalType === 'PENDING' && nftItem?.auction?.status === 'ENDED') {
            setEndAuctionModalType('SUCCESS');
        }
    }, [nftItem, endAuctionModalType]);

    const fetchAuctionGloballyPaused = useCallback(async () => {
        const auctionContract =
            contracts?.auctionV2 && isV2Auction(nftItem?.auction?.contractAddress?.address)
                ? contracts.auctionV2
                : contracts?.auction;
        const auctionId = nftItem?.auction?.auctionId;
        if (auctionContract && auctionId) {
            try {
                const isGloballyPaused = await auctionContract.globalPaused();
                setIsAuctionGloballyPaused(isGloballyPaused);
            } catch (e) {
                setIsAuctionGloballyPaused(false);
            }
        }
    }, [contracts, nftItem]);

    useEffect(() => {
        fetchAuctionGloballyPaused();
    }, [fetchAuctionGloballyPaused]);

    const endAuction = useCallback(async () => {
        const auctionContract =
            contracts?.auctionV2 && isV2Auction(nftItem?.auction?.contractAddress?.address)
                ? contracts.auctionV2
                : contracts?.auction;
        const auctionId = nftItem?.auction?.auctionId;
        if (auctionContract && auctionId && library) {
            try {
                const signer = await library.getSigner();
                setEndAuctionPending(true);
                const transaction = await auctionContract.connect(signer).endAuction(auctionId);
                setEndAuctionModalOpen(true);
                setEndAuctionModalType('PENDING');
                await transaction.wait();
                setEndAuctionPending(false);
            } catch (e: $TSFixMe) {
                setEndAuctionModalOpen(false);
                setEndAuctionPending(false);
                if (e.code !== 4001 /* silently fail user rejection */) {
                    setHasEndAuctionError(true);
                }
            }
        }
    }, [contracts, nftItem, library]);

    let creatorDisplay: 'text' | 'link' | 'none' = 'link';
    if (!showCreator) {
        creatorDisplay = 'none';
    } else if (profile?.isItemCreator) {
        creatorDisplay = 'text';
    }

    return (
        <>
            <div className={styles.detailsSection}>
                <div className={styles.contentTopContainer}>
                    <div className={styles.detailsContainer}>
                        <NftItemTileItemDetails
                            nftItem={nftItem}
                            fireTracking={fireTracking}
                            creatorDisplay={creatorDisplay}
                            titleDisplay={showCtas && !nftItem.wasBurned ? 'link' : 'text'}
                        />
                        {showAuction && (
                            <NftItemTileAuctionDetails
                                nftAuction={nftAuction}
                                nftItem={nftItem}
                                currency={currency}
                                showOwner={false}
                                persistedWalletAddress={bidder?.bid.bidderAddress}
                            />
                        )}
                    </div>
                    {profile?.isUserProfile && (
                        <NftMarketProvider
                            viewer={viewer}
                            entryTrackingLabel="NFT public profile tooltip"
                            nftItem={nftItem}
                        >
                            <NftItemTileTooltip
                                viewer={viewer}
                                nftItem={nftItem}
                                nftItemDraft={null}
                                isMobile={isMobile}
                            />
                        </NftMarketProvider>
                    )}
                </div>
                <div className={styles.ctaContainer}>
                    {/* view, bid, or settle */}
                    {showCtas && nftItem.pdpUrl && bidder?.action && (
                        <Cta msgKey={bidder.action} href={nftItem.pdpUrl} onClick={fireTracking} />
                    )}
                    {/* checking auction status is unnecessary because once an item has an auction,
                    that auction owns the item and therefor user.isItemOwner = false */}
                    {!viewer.nftBenignNeglect &&
                        showCtas &&
                        !auction?.isCountdownFinished &&
                        user?.isItemOwner &&
                        profile?.isUserProfile && (
                            <Cta
                                msgKey="auction"
                                href={`${manageUrl}list/`}
                                onClick={fireTracking}
                            />
                        )}
                    {showCtas && auction?.isCountdownFinished && user?.isAuctionSeller && (
                        <Cta
                            msgKey="endAuction"
                            onClick={() => {
                                fireTracking?.();
                                endAuction();
                            }}
                            disabled={endAuctionPending || !!isAuctionGloballyPaused}
                        />
                    )}
                    {hasEndAuctionError && (
                        <div className={styles.bidErrorWrapper}>
                            <ExclamationCircle className={styles.exclamationIcon} />
                            <p>{intl.formatMessage(messages.endAuctionError)}</p>
                        </div>
                    )}
                </div>
                {user?.isBidder && bidder && !auction?.isCountdownFinished && nftItem.pdpUrl && (
                    <NftItemTileMostRecentBidLeaderCta
                        currency={currency}
                        onClick={fireTracking}
                        pdpUrl={nftItem.pdpUrl}
                        isLeader={!!bidder.isLeader}
                        {...bidder.bid}
                    />
                )}
                <NftItemTileTokenDetails showToken={!nftItem?.isERC1155} nftItem={nftItem} />
            </div>
            <EndAuctionModal
                modalType={endAuctionModalType}
                modalOpen={endAuctionModalOpen}
                isBuyerClaiming={false}
                closeModal={() => {
                    setEndAuctionModalOpen(false);
                    if (endAuctionModalType === 'SUCCESS') {
                        setEndAuctionModalType(null);
                    }
                }}
                viewer={viewer}
            />
        </>
    );
};
